import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface MediaPlayerProps {
  src: string | null | undefined; // can be a storage path like "user/123.mp4" or a full URL
  bucket?: string; // defaults to 'recordings'
  poster?: string | null;
  title?: string;
  className?: string;
  autoPlay?: boolean;
  controls?: boolean;
}

// Try to extract a storage path from a full URL (public URL or signed URL)
function extractPathFromUrl(url: string, bucket: string): string | null {
  try {
    const idx = url.indexOf(`/${bucket}/`);
    if (idx !== -1) {
      return url.substring(idx + bucket.length + 2); // after "bucket/"
    }
    // Fallback: look for "/object/" then bucket name
    const bucketIdx = url.indexOf(bucket + '/');
    if (bucketIdx !== -1) return url.substring(bucketIdx + bucket.length + 1);
    return null;
  } catch {
    return null;
  }
}

export const MediaPlayer: React.FC<MediaPlayerProps> = ({
  src,
  bucket = 'recordings',
  poster,
  title,
  className,
  autoPlay = false,
  controls = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const isHls = useMemo(() => (src || '').toLowerCase().endsWith('.m3u8'), [src]);

  useEffect(() => {
    let mounted = true;
    const resolveUrl = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!src) {
          throw new Error('No media source provided');
        }
        let pathOrUrl = src;
        // If looks like full URL, extract path; otherwise treat as path
        if (src.startsWith('http')) {
          const extracted = extractPathFromUrl(src, bucket);
          if (extracted) {
            pathOrUrl = extracted;
          }
        }

        if (!pathOrUrl.startsWith('http')) {
          // Treat as private storage path: create a signed URL for playback
          const { data, error } = await supabase
            .storage
            .from(bucket)
            .createSignedUrl(pathOrUrl, 60 * 60); // 1 hour
          if (error) throw error;
          if (!mounted) return;
          setPlaybackUrl(data.signedUrl);
        } else {
          // Already a URL
          setPlaybackUrl(pathOrUrl);
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load media');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    resolveUrl();
    return () => {
      mounted = false;
    };
  }, [src, bucket]);

  useEffect(() => {
    // Initialize HLS only when needed
    if (!videoRef.current || !playbackUrl) return;

    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(playbackUrl);
      hls.attachMedia(videoRef.current);
      return () => {
        hls.destroy();
      };
    } else if (isHls && videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      videoRef.current.src = playbackUrl;
    }
  }, [isHls, playbackUrl]);

  if (loading) {
    return (
      <div className={cn('aspect-video flex items-center justify-center rounded-md bg-muted', className)}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !playbackUrl) {
    return (
      <div className={cn('aspect-video flex items-center justify-center rounded-md bg-muted text-sm text-muted-foreground', className)}>
        {error || 'Unable to load media'}
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className={cn('w-full h-auto rounded-md', className)}
      poster={poster || undefined}
      title={title}
      controls={controls}
      autoPlay={autoPlay}
      playsInline
      preload="metadata"
      // For non-HLS sources, we set the src directly
      src={!isHls ? playbackUrl : undefined}
    />
  );
};

export default MediaPlayer;
